import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { JOBS_KEY } from './useJobs';
import type { ScrapeLead, ScrapeLogEntry, ScrapeParams, StreamStatus } from '../types';

// Same-origin API base. VITE_API_BASE_URL is intentionally EMPTY in prod, and
// `??` does NOT override an empty string — so we must build the full `/api/...`
// path here (matching the axios convention) rather than defaulting the base to
// '/api'. Defaulting to '/api' + `${base}/scrape` collapsed to `/scrape` when
// the env var was '', hitting the SPA fallback (text/html) and breaking SSE.
const API_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '';
const MAX_LOGS = 400;
const MAX_LEADS = 600;

export interface ScrapeStreamState {
  status: StreamStatus;
  message: string;
  logs: ScrapeLogEntry[];
  leads: ScrapeLead[];
  savedCount: number;
  dupCount: number;
  elapsedSec: number;
}

const INITIAL: ScrapeStreamState = {
  status: 'idle',
  message: '',
  logs: [],
  leads: [],
  savedCount: 0,
  dupCount: 0,
  elapsedSec: 0,
};

/**
 * EventSource-based scrape runner. The live stream is RUNTIME state (not React
 * Query). React Query is invalidated EXACTLY ONCE on a terminal state
 * (complete / stopped / error) so persistent job history refreshes.
 *
 * Streaming-stability guarantees:
 *  - Never auto-reconnects (a one-shot scrape must not restart): we close() on
 *    every terminal event and treat any onerror as terminal.
 *  - A `terminal` ref guards against double-finalisation / duplicate invalidations.
 *  - The connection + timer are always torn down on unmount.
 */
export function useScrapeStream() {
  const queryClient = useQueryClient();
  const esRef = useRef<EventSource | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const settleRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startedAtRef = useRef(0);
  const logIdRef = useRef(0);
  const terminalRef = useRef(false);
  const [state, setState] = useState<ScrapeStreamState>(INITIAL);

  const teardown = useCallback(() => {
    if (esRef.current) {
      esRef.current.close();
      esRef.current = null;
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const finalize = useCallback(
    (status: StreamStatus, message: string, invalidateDelayMs = 0) => {
      if (terminalRef.current) return;
      terminalRef.current = true;
      teardown();
      setState((s) => ({ ...s, status, message: message || s.message }));
      // Invalidate persistent history EXACTLY ONCE.
      if (invalidateDelayMs > 0) {
        settleRef.current = setTimeout(() => queryClient.invalidateQueries({ queryKey: JOBS_KEY }), invalidateDelayMs);
      } else {
        queryClient.invalidateQueries({ queryKey: JOBS_KEY });
      }
    },
    [queryClient, teardown],
  );

  const start = useCallback(
    (params: ScrapeParams) => {
      if (esRef.current) return; // a stream is already active — guard against duplicates
      terminalRef.current = false;
      logIdRef.current = 0;
      startedAtRef.current = Date.now();
      setState({ ...INITIAL, status: 'connecting' });

      const qs = new URLSearchParams({
        niche: params.niche,
        location: params.location,
        minRating: String(params.minRating),
        maxResults: String(params.maxResults),
        minReviews: String(params.minReviews),
        websiteFilter: params.websiteFilter,
        ageFilter: params.ageFilter,
        requirePhone: params.requirePhone ? '1' : '0',
        requireEmail: params.requireEmail ? '1' : '0',
        requireAddress: params.requireAddress ? '1' : '0',
      }).toString();

      const es = new EventSource(`${API_BASE}/api/scrape?${qs}`, { withCredentials: true });
      esRef.current = es;

      timerRef.current = setInterval(() => {
        setState((s) => ({ ...s, elapsedSec: Math.round((Date.now() - startedAtRef.current) / 1000) }));
      }, 1000);

      es.onmessage = (ev: MessageEvent) => {
        let data: { type?: string; [k: string]: unknown };
        try {
          data = JSON.parse(ev.data);
        } catch {
          return;
        }
        switch (data.type) {
          case 'status':
            setState((s) => ({ ...s, status: 'running', message: String(data.message ?? '') }));
            break;
          case 'log': {
            const log = (data.log ?? {}) as { status?: string; message?: string };
            const entry: ScrapeLogEntry = { id: ++logIdRef.current, status: log.status ?? 'info', message: log.message ?? '' };
            const isDup = /^Duplicate skipped/i.test(entry.message);
            setState((s) => ({
              ...s,
              logs: [...s.logs.slice(-(MAX_LOGS - 1)), entry],
              dupCount: isDup ? s.dupCount + 1 : s.dupCount,
            }));
            break;
          }
          case 'lead': {
            const lead = data.lead as ScrapeLead;
            setState((s) => ({ ...s, leads: [lead, ...s.leads].slice(0, MAX_LEADS), savedCount: s.savedCount + 1 }));
            break;
          }
          case 'complete':
            setState((s) => ({
              ...s,
              savedCount: typeof data.saved === 'number' ? data.saved : s.savedCount,
              dupCount: typeof data.duplicates === 'number' ? data.duplicates : s.dupCount,
            }));
            // Server has already persisted Jobs.finish before sending 'complete'.
            finalize('completed', `Completed — ${data.saved ?? 0} saved, ${data.duplicates ?? 0} duplicate(s).`);
            break;
          case 'error':
            finalize('error', String(data.message ?? 'The scrape failed.'));
            break;
        }
      };

      es.onerror = () => {
        // Never allow EventSource auto-reconnect for a one-shot scrape.
        if (!terminalRef.current) {
          // Distinguish "never connected" (likely a busy/stuck prior job or auth)
          // from a mid-stream drop, so the message is actionable.
          setState((s) => {
            const neverStarted = s.logs.length === 0 && s.savedCount === 0;
            const msg = neverStarted
              ? "Couldn't start the scan stream. A previous scan may still be finishing — wait a few seconds and try again."
              : 'The scrape connection was lost.';
            // finalize reads message from state if none passed; set it here first.
            return { ...s, message: msg };
          });
          finalize('error', '');
        }
      };
    },
    [finalize],
  );

  const stop = useCallback(() => {
    // Closing the connection triggers the backend `req.on('close')` abort, which
    // finishes the job as 'stopped'. Delay the single invalidation briefly so the
    // server has settled the status before history refetches.
    finalize('stopped', 'Scan stopped.', 800);
  }, [finalize]);

  const reset = useCallback(() => {
    if (esRef.current) return; // do not reset while active
    setState(INITIAL);
  }, []);

  // Always tear down on unmount (navigation away, refresh).
  useEffect(() => {
    return () => {
      teardown();
      if (settleRef.current) clearTimeout(settleRef.current);
    };
  }, [teardown]);

  const isActive = state.status === 'connecting' || state.status === 'running';

  return { ...state, isActive, start, stop, reset };
}
